import { Button, DatePicker, Modal, Space, Typography, message } from "antd";
import { Dayjs } from "dayjs";
import React from "react";
import { useFetcher, useRevalidator } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { INTENTS } from "~/utils";
import { createFinanceReportPeriod } from "~/pages/FinancePages/hooks/financeReportAutoRefresh";

export type BulkRequestRentPaymentsButtonProps = {
  actionPath: string;
  defaultYear?: number;
  defaultMonth?: number;
  propertyID?: string;
  disabled?: boolean;
};

type BulkRequestRentPaymentsResult = {
  created: Array<{ id: string }>;
  skipped: Array<{ leaseID: string; unitID: string; reason: string }>;
  summary: {
    candidateCount: number;
    createdCount: number;
    skippedCount: number;
  };
};

export const BulkRequestRentPaymentsButton = ({
  actionPath,
  defaultYear = new Date().getFullYear(),
  defaultMonth = new Date().getMonth(),
  propertyID,
  disabled = false,
}: BulkRequestRentPaymentsButtonProps) => {
  const { t } = useTranslation();
  const fetcher = useFetcher();
  const revalidator = useRevalidator();
  const [open, setOpen] = React.useState(false);
  const [selectedPeriod, setSelectedPeriod] = React.useState<Dayjs | null>(
    () => createFinanceReportPeriod(defaultYear, defaultMonth),
  );
  const lastActionKeyRef = React.useRef<string | null>(null);

  const isSubmitting = fetcher.state !== "idle";

  React.useEffect(() => {
    setSelectedPeriod(createFinanceReportPeriod(defaultYear, defaultMonth));
  }, [defaultMonth, defaultYear]);

  React.useEffect(() => {
    if (fetcher.state !== "idle" || fetcher.data == null) {
      return;
    }

    const period = selectedPeriod;
    if (!period) return;

    const actionKey = [
      period.year(),
      period.month(),
      propertyID ?? "portfolio",
    ].join(":");

    if (lastActionKeyRef.current === actionKey) {
      return;
    }
    lastActionKeyRef.current = actionKey;

    const result = fetcher.data as
      | BulkRequestRentPaymentsResult
      | { success?: false; error?: string };

    if (!("summary" in result)) {
      message.error(
        ("error" in result && result.error) ||
          t("Something_went_wrong_Please_try_again"),
      );
      return;
    }

    const { createdCount, skippedCount } = result.summary;
    if (createdCount > 0) {
      message.success(
        t("Bulk_rent_payment_requests_created", {
          count: createdCount,
          skipped: skippedCount,
        }),
      );
    } else {
      message.info(t("Bulk_rent_payment_requests_none"));
    }
    setOpen(false);
    revalidator.revalidate();
  }, [fetcher.data, fetcher.state, propertyID, revalidator, selectedPeriod, t]);

  const handleOpen = () => {
    lastActionKeyRef.current = null;
    setSelectedPeriod(createFinanceReportPeriod(defaultYear, defaultMonth));
    setOpen(true);
  };

  const handleConfirm = () => {
    if (!selectedPeriod) {
      message.error(t("Select_year_and_month"));
      return;
    }

    lastActionKeyRef.current = null;
    const formData: Record<string, string> = {
      intent: INTENTS._bulk_request_rent_payments,
      year: String(selectedPeriod.year()),
      month: String(selectedPeriod.month()),
    };
    if (propertyID) {
      formData.propertyID = propertyID;
    }

    fetcher.submit(formData, { method: "POST", action: actionPath });
  };

  return (
    <>
      <Button type="primary" ghost onClick={handleOpen} disabled={disabled}>
        {t("Bulk_request_rent_payments")}
      </Button>

      <Modal
        open={open}
        title={t("Bulk_request_rent_payments")}
        onCancel={() => setOpen(false)}
        onOk={handleConfirm}
        okText={t("Bulk_request_rent_payments_confirm")}
        confirmLoading={isSubmitting}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            {t("Bulk_request_rent_payments_description")}
          </Typography.Paragraph>
          <div>
            <Typography.Text>{t("Select_year_and_month")}</Typography.Text>
            <DatePicker
              picker="month"
              value={selectedPeriod}
              onChange={(date) => setSelectedPeriod(date)}
              style={{ width: "100%", marginTop: 8 }}
              disabled={isSubmitting}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};
